import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ShieldCheck, Zap } from 'lucide-react';
import { CTAButton } from './CTAButton';
const faqs = [{
  q: '¿Necesito comprar herramientas?',
  a: 'No, el Blueprint se enfoca en servicios que usan las herramientas del vecino o cosas que ya tienes en casa.'
}, {
  q: '¿Es seguro?',
  a: 'Sí, incluimos un protocolo de seguridad para que siempre estés a una distancia visual segura.'
}, {
  q: '¿Y si mi hijo es muy tímido?',
  a: "El capítulo de 'Guiones Maestros' está diseñado específicamente para romper el hielo paso a paso."
}, {
  q: '¿Funciona en apartamentos?',
  a: 'Absolutamente, el método se adapta a edificios o casas unifamiliares.'
}, {
  q: '¿Cuánto tiempo toma?',
  a: 'El plan está diseñado para ejecutarse en un solo fin de semana.'
}, {
  q: '¿Es legal?',
  a: 'Sí, en EE.UU. las tareas menores de vecindario por parte de menores son una tradición cultural y legalmente aceptable.'
}];
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-35">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-36">
        <span className="text-lg font-heading font-bold text-navy pr-4" data-id="element-37">
          {q}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 text-orange" data-id="element-38">
          <ChevronDown className="w-6 h-6" data-id="element-39" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-40">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-41">
            <p className="pb-6 text-gray-600 font-body" data-id="element-42">{a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function BottomSection() {
  return <section className="py-20 md:py-32 bg-white px-4" data-id="element-43">
      <div className="max-w-4xl mx-auto" data-id="element-44">
        {/* FAQ Section */}
        <div className="mb-24" data-id="element-45">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-center text-navy mb-12" data-id="element-46">
            Preguntas Frecuentes
          </h2>
          <div className="bg-gray-light rounded-2xl p-6 md:p-10" data-id="element-47">
            {faqs.map((faq, idx) => <FAQItem key={idx} q={faq.q} a={faq.a} data-id="element-48" />)}
          </div>
        </div>

        {/* Offer + Price Block */}
        <motion.div id="pricing" initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-navy rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl relative overflow-hidden border-4 border-orange scroll-mt-24" data-id="element-49">
          {/* Decorative background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-50" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-51" />

          <div className="relative z-10" data-id="element-52">
            <div className="flex justify-center mb-6" data-id="element-53">
              <div className="bg-orange/20 text-orange-light p-3 rounded-full" data-id="element-54">
                <Zap className="w-8 h-8" data-id="element-55" />
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-heading font-black mb-6" data-id="element-56">
              Dale a tu hijo la ventaja competitiva que la escuela ignora.
            </h2>

            <p className="text-lg md:text-xl font-body text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-57">
              Por el precio de una salida a comer pizza, puedes darle la
              herramienta que definirá su relación con el dinero de por vida.
            </p>

            <div className="bg-white/10 rounded-2xl p-6 mb-10 inline-block border border-white/20" data-id="element-58">
              <div className="inline-block bg-red-500 text-white font-heading font-bold text-sm px-4 py-1 rounded-full mb-3 uppercase tracking-wider animate-pulse" data-id="element-59">
                🔥 76% de descuento — ¡Oferta limitada!
              </div>
              <div className="text-sm font-heading text-gray-300 uppercase tracking-widest mb-2" data-id="element-60">
                Inversión única
              </div>
              <div className="flex items-center justify-center gap-3 mb-1" data-id="element-61">
                <span className="text-2xl font-heading font-bold text-gray-400 line-through" data-id="element-62">
                  $27
                </span>
              </div>
              <div className="text-6xl font-heading font-black text-orange flex items-start justify-center" data-id="element-63">
                <span className="text-3xl mt-2 mr-1" data-id="element-64">$</span>6.39
                <span className="text-2xl mt-6 ml-2 text-gray-300" data-id="element-65">USD</span>
              </div>
              <div className="mt-3 text-sm font-body text-green-400 font-semibold" data-id="element-66">
                ¡Te ahorras $20.61 hoy!
              </div>
            </div>

            <CTAButton href="https://macmstore.com/cart/63445867266417:1" className="mb-6" data-id="element-67" />

            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 font-body" data-id="element-68">
              <ShieldCheck className="w-4 h-4" data-id="element-69" />
              <span data-id="element-70">Pago 100% seguro y encriptado</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}